import axios from 'axios';
import { BASE_API_URL } from './config';
import TokenService from './token.service';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';

// Shared store read by App.js 60s interval to show expiry toasts
export const expiryMsgStore = { current: null };

export const showExpiryToast = (message) => {
    toast.warning(
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.6', fontSize: '13px', padding: '2px 0' }}>
            {message}
        </div>,
        {
            toastId: 'expiry-message',
            autoClose: 8000,
            closeButton: OkCloseButton,
            style: { minWidth: '720px', maxWidth: '820px', width: 'auto' },
        }
    );
};

export const OkCloseButton = ({ closeToast }) => (
    <button
        onClick={closeToast}
        style={{
            background: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            color: '#b45309',
            fontWeight: '600',
            fontSize: '12px',
            padding: '4px 14px',
            letterSpacing: '0.5px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            transition: 'background 0.15s',
            alignSelf: 'center',
            flexShrink: 0,
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
    >
        OK
    </button>
);

// Endpoints that must never trigger a token-refresh retry
const SKIP_REFRESH_URLS = [
    '/api/v1/user/login',
    '/api/v1/user/renewAccessToken',
];
 
/**
 * Creates a pre-configured axios instance with:
 *  - Automatic CSRF token injection on every request
 *  - Transparent silent token refresh on 401 responses
 *  - A shared queue so parallel requests wait for a single refresh attempt
 *
 * @param {string} contentType  Default Content-Type header for the instance.
 *                              Use 'multipart/form-data' for file upload endpoints.
 * @returns {import('axios').AxiosInstance}
 */
export function createApiInstance(contentType = 'application/json') {
    const instance = axios.create({
        baseURL: BASE_API_URL,
        withCredentials: true,
        headers: { 'Content-Type': contentType },
    });
 
    // Each instance has its own refresh-gate so multipart uploads and JSON
    // requests don't share state and accidentally block each other.
    let isRefreshing = false;
    let failedQueue = [];
 
    const processQueue = (error) => {
        failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve()));
        failedQueue = [];
    };
 
    // ── Request interceptor ────────────────────────────────────────────────
    instance.interceptors.request.use(
        (config) => {
            const csrf = Cookies.get('csrfToken');
            if (csrf) config.headers['x-csrf-token'] = csrf;
            return config;
        },
        (error) => Promise.reject(error)
    );
 
    // ── Response interceptor ───────────────────────────────────────────────
    instance.interceptors.response.use(
        (response) => {
            const msg = response.headers['x-expiry-message'];
            if (msg !== undefined) {
                const newMsg = msg ? decodeURIComponent(msg) : null;
                if (newMsg !== expiryMsgStore.current) {
                    expiryMsgStore.current = newMsg;
                    if (newMsg) {
                        showExpiryToast(newMsg);
                    }
                }
            }
            return response;
        },
        async (error) => {
            // No response at all (network error, timeout, etc.)
            if (!error?.response || !error?.config) return Promise.reject(error);

            const original = error.config;
            const status = error.response.status;
            const url = original.url ?? '';

            // Server-forced logout (temporary access expired, or the user/org
            // was deactivated) — bypass the refresh flow entirely and log the
            // user out immediately. Keyed on the forceLogout flag itself, not
            // a specific status code: deactivation returns 401 (same as a
            // plain expired token) while temp-expiry returns 423, but both
            // carry forceLogout — checking the flag alone covers all of them
            // instead of only the one case that happens to be 423.
            if (error.response.data?.forceLogout) {
                const message = error.response.data?.message;
                if (message) toast.error(message);
                TokenService.removeUser();
                window.location.href = '/login';
                return Promise.reject(error);
            }
 
            // Never retry auth-related endpoints — avoids infinite loops
            const shouldSkip = SKIP_REFRESH_URLS.some((u) => url.includes(u));
            if (shouldSkip || status !== 401 || original._retry) {
                return Promise.reject(error);
            }
 
            // Queue subsequent 401s while a refresh is already in flight
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(() => instance(original))
                    .catch((err) => Promise.reject(err));
            }
 
            original._retry = true;
            isRefreshing = true;
 
            try {
                // For multipart instances the refresh must use JSON — use a
                // fresh plain axios call to avoid content-type conflicts.
                if (contentType === 'multipart/form-data') {
                    const csrf = Cookies.get('csrfToken');
                    await axios.post(
                        `${BASE_API_URL}/api/v1/user/renewAccessToken`,
                        {},
                        {
                            withCredentials: true,
                            headers: {
                                'Content-Type': 'application/json',
                                ...(csrf && { 'x-csrf-token': csrf }),
                            },
                        }
                    );
                } else {
                    await instance.post('/api/v1/user/renewAccessToken');
                }
 
                // Refresh succeeded — update CSRF header and replay queued requests
                original.headers['x-csrf-token'] = Cookies.get('csrfToken');
                processQueue(null);
                return instance(original);
            } catch (refreshError) {
                processQueue(refreshError);
                TokenService.removeUser();
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }
    );
 
    return instance;
}